import { Component, EventEmitter, HostListener, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { ICONS } from "src/app/_helpers/icons.constants";
import { SmartImportPageGroup } from "src/app/_models/credit-card/smart-import";

@Component({
  selector: "smart-import-reader",
  templateUrl: "./smart-import-reader.component.html",
  standalone: true,
  imports: [CommonModule, TranslateModule, NgxExtendedPdfViewerModule],
})
export class SmartImportReaderComponent {
  // Read-only viewer of the selected group's own pages — no editing, no other groups.
  // Chevrons/keyboard nav cycle within group.pageNumbers only (spec US-06).
  @Input() group: SmartImportPageGroup | null = null;
  @Input() fileUrl: string | null = null;
  @Input() pageIndex = 0;
  @Output() pageIndexChange = new EventEmitter<number>();
  @Output() closed = new EventEmitter<void>();

  public readonly ICONS = ICONS;
  viewerLoading = true;

  get currentPageNumber(): number {
    return this.group?.pageNumbers?.[this.pageIndex] ?? 1;
  }

  get isFirstPage(): boolean {
    return this.pageIndex <= 0;
  }

  get isLastPage(): boolean {
    return !this.group || this.pageIndex >= this.group.pageNumbers.length - 1;
  }

  next(): void {
    if (!this.isLastPage) {
      this.pageIndexChange.emit(this.pageIndex + 1);
    }
  }

  prev(): void {
    if (!this.isFirstPage) {
      this.pageIndexChange.emit(this.pageIndex - 1);
    }
  }

  // ngx-extended-pdf-viewer defaults to continuous scroll, so the visible page can change
  // from scrolling as well as from next()/prev() — this keeps pageIndex (and so the
  // header subtitle) in sync either way, as long as the scrolled-to page belongs to this group.
  onViewerPageChange(pageNumber: number): void {
    const index = this.group?.pageNumbers?.indexOf(pageNumber) ?? -1;
    if (index >= 0 && index !== this.pageIndex) {
      this.pageIndexChange.emit(index);
    }
  }

  close(): void {
    this.closed.emit();
  }

  onViewerLoadingStarts(): void {
    this.viewerLoading = true;
  }

  onViewerLoaded(): void {
    this.viewerLoading = false;
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "ArrowRight") {
      this.next();
    } else if (event.key === "ArrowLeft") {
      this.prev();
    }
  }
}
